import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneSheet } from '../chats/phone/PhoneSheet';
import { FeatherIcon } from '../components/FeatherIcon';
import { Spinner } from '../components/Spinner';
import { canCopyToSend } from './copyToSend';
import type { ChatMessage } from './messages';
import { messageRights, type MessageActions } from './messageRights';
import { splitQuote } from './quote';

/** One line for the sheet's preview: the text, or what the message holds. */
function previewOf(m: ChatMessage, t: (k: string, o?: Record<string, unknown>) => string): string {
  const body = splitQuote(m.text).body.trim();
  if (body) return body;
  if (m.tip) return t('thread.tip');
  if (m.media.length) return t('menu.mediaCount', { count: m.media.length });
  return '';
}

/**
 * Phones (ADR 0035): press and hold a message for this sheet instead of the
 * desktop's hover menu. The message on top, then what can be done to it.
 */
export function MessageSheet({ m, onClose, ...actions }: { m: ChatMessage | null; onClose: () => void } & MessageActions) {
  const { t } = useTranslation('chat');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  // The sheet keeps the last message while it slides away.
  const [shown, setShown] = useState<ChatMessage | null>(m);
  useEffect(() => {
    if (m) setShown(m);
    setConfirming(false);
    setFailed(null);
  }, [m]);
  const msg = m ?? shown;
  const rights = msg ? messageRights(msg, actions) : null;
  const run = (fn: () => void) => () => {
    fn();
    onClose();
  };

  return (
    <PhoneSheet open={!!m} onClose={onClose} label={t('menu.sheet')}>
      {msg && rights && (
        <div className="rc-msheet">
          <div className="rc-msheet-preview">{previewOf(msg, t)}</div>
          {confirming ? (
            <div className="rc-msheet-confirm" role="alertdialog" aria-label={t('menu.unsendConfirm')}>
              <span>{t('menu.unsendConfirm')}</span>
              {failed && <span className="rc-error">{failed}</span>}
              <div className="rc-msheet-confirm-actions">
                <button type="button" onClick={() => setConfirming(false)} disabled={busy}>
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="is-danger"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setFailed(null);
                    try {
                      await actions.onUnsend!(msg.id);
                      onClose();
                    } catch (err) {
                      setFailed((err as Error).message);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {busy ? <Spinner size={12} label={t('menu.unsend')} /> : t('menu.unsend')}
                </button>
              </div>
            </div>
          ) : (
            <div className="ph-menu" role="menu" aria-label={t('menu.sheet')}>
              {(rights.like || rights.unlike) && (
                <button type="button" role="menuitem" className="ph-menu-item" onClick={run(() => void actions.onLike!(msg.id, rights.like).catch(() => undefined))}>
                  <FeatherIcon name="heart" size={19} />
                  {rights.like ? t('menu.like') : t('menu.unlike')}
                </button>
              )}
              {actions.onReply && (
                <button type="button" role="menuitem" className="ph-menu-item" onClick={run(() => actions.onReply!(msg))}>
                  <FeatherIcon name="corner-up-left" size={19} />
                  {t('menu.reply')}
                </button>
              )}
              {msg.text && (
                <button type="button" role="menuitem" className="ph-menu-item" onClick={run(() => void navigator.clipboard?.writeText(splitQuote(msg.text).body).catch(() => undefined))}>
                  <FeatherIcon name="copy" size={19} />
                  {t('menu.copy')}
                </button>
              )}
              {actions.onCopyToSend && canCopyToSend(msg) && (
                <button type="button" role="menuitem" className="ph-menu-item" onClick={run(() => actions.onCopyToSend!(msg))}>
                  <FeatherIcon name="clipboard" size={19} />
                  {t('menu.copyToSend')}
                </button>
              )}
              {actions.onTranslate && msg.text && (
                <button type="button" role="menuitem" className="ph-menu-item" onClick={run(() => actions.onTranslate!(msg))}>
                  <FeatherIcon name="globe" size={19} />
                  {t('menu.translate')}
                </button>
              )}
              {rights.unsend && (
                <button type="button" role="menuitem" className="ph-menu-item is-danger" onClick={() => setConfirming(true)}>
                  <FeatherIcon name="trash-2" size={19} />
                  {t('menu.unsend')}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </PhoneSheet>
  );
}
